import { useMemo } from "react";
import { useUserHasRoleOnPlatformOwner } from "@coworker/apprestructured/src/login/hooks/useUserHasRoleOnPlatformOwner";
import { useGraphDisplayNames } from "@coworker/app/src/hooks/API/displayName.service";
import { useAtkomstCountries } from "./useAtkomstCountries";
import { useAtkomstNodeUsersMulti } from "./useAtkomstNodeUsers";
import { useAtkomstStoresByCountry } from "./useAtkomstStoresByCountry";
import {
  PAGE_SIZE,
  SortCol,
  getUserCountryNames,
  isCoworker,
  isCountrySU,
  isStoreSU,
} from "../utils/atkomstUsersUtils";

interface Params {
  selectedNodeRefs: string[];
  searchQuery: string;
  sortCol: SortCol | null;
  sortDir: "asc" | "desc";
  page: number;
}

export function useAtkomstUsersData({
  selectedNodeRefs,
  searchQuery,
  sortCol,
  sortDir,
  page,
}: Params) {
  const { data: isPlatformOwner, isLoading: isCheckingRole } =
    useUserHasRoleOnPlatformOwner();

  const { data: countries = [], isLoading: isLoadingCountries } =
    useAtkomstCountries(!!isPlatformOwner);

  const selectedCountryIds = useMemo(
    () =>
      selectedNodeRefs
        .filter((ref) => ref.startsWith("RU."))
        .map((ref) => ref.slice(3)),
    [selectedNodeRefs]
  );

  const { storesByCountry: rawStoresByCountry, isLoading: isLoadingStores } =
    useAtkomstStoresByCountry(selectedCountryIds);

  const { countryNodes, nodeNameByRef, storesByCountry } = useMemo(() => {
    const sortedCountries = [...countries].sort((a, b) =>
      a.name.localeCompare(b.name)
    );
    const countryRefs = sortedCountries.map((c) => ({
      ref: `RU.${c.isoCode}`,
      name: c.name,
    }));
    const map = new Map<string, string>();
    countryRefs.forEach((c) => map.set(c.ref, c.name));
    const byCountry = new Map<
      string,
      { ref: string; name: string; countryId: string }[]
    >();
    rawStoresByCountry.forEach((stores, countryId) => {
      const storeRefs = stores
        .filter((s) => !s.storeId.includes("#"))
        .map((s) => ({ ref: `STO.${s.storeId}`, name: s.name, countryId }))
        .sort((a, b) => a.name.localeCompare(b.name));
      storeRefs.forEach((s) => map.set(s.ref, s.name));
      byCountry.set(countryId, storeRefs);
    });
    return {
      countryNodes: countryRefs,
      nodeNameByRef: map,
      storesByCountry: byCountry,
    };
  }, [countries, rawStoresByCountry]);

  const {
    data: mergedUsers,
    isLoading: isLoadingUsers,
    isFetching: isFetchingUsers,
    isError: isErrorUsers,
  } = useAtkomstNodeUsersMulti(selectedNodeRefs);

  const countryNameById = useMemo(
    () => new Map(countryNodes.map((c) => [c.ref.slice(3), c.name])),
    [countryNodes]
  );

  const storeRefToCountryId = useMemo(() => {
    const map = new Map<string, string>();
    storesByCountry.forEach((storeRefs) => {
      storeRefs.forEach((store) => map.set(store.ref, store.countryId));
    });
    return map;
  }, [storesByCountry]);

  const filteredUsers = useMemo(() => {
    if (!searchQuery.trim()) return mergedUsers;
    const q = searchQuery.toLowerCase();
    return mergedUsers.filter(
      (u) =>
        u.email.toLowerCase().includes(q) ||
        u.roles.some((r) => r.toLowerCase().includes(q))
    );
  }, [mergedUsers, searchQuery]);

  const emailsForDisplayNames = useMemo(
    () => mergedUsers.map((u) => u.email),
    [mergedUsers]
  );
  const { data: displayNames } = useGraphDisplayNames(emailsForDisplayNames);

  const fullyFilteredUsers = useMemo(() => {
    if (!searchQuery.trim() || !displayNames) return filteredUsers;
    const q = searchQuery.toLowerCase();
    return filteredUsers.filter((u) => {
      const name = displayNames[u.email] ?? "";
      return (
        u.email.toLowerCase().includes(q) ||
        name.toLowerCase().includes(q) ||
        u.roles.some((r) => r.toLowerCase().includes(q))
      );
    });
  }, [filteredUsers, displayNames, searchQuery]);

  const sortedUsers = useMemo(() => {
    if (!sortCol) return fullyFilteredUsers;
    if (sortCol === "countryName") {
      return [...fullyFilteredUsers].sort((a, b) => {
        const aStr =
          getUserCountryNames(a, countryNameById, storeRefToCountryId).join(
            ", "
          ) || "-";
        const bStr =
          getUserCountryNames(b, countryNameById, storeRefToCountryId).join(
            ", "
          ) || "-";
        const diff = aStr.localeCompare(bStr);
        if (diff !== 0) return sortDir === "asc" ? diff : -diff;
        return a.email.localeCompare(b.email);
      });
    }
    const hasRole =
      sortCol === "store"
        ? isStoreSU
        : sortCol === "country"
          ? isCountrySU
          : isCoworker;
    return [...fullyFilteredUsers].sort((a, b) => {
      const aHas = hasRole(a) ? 0 : 1;
      const bHas = hasRole(b) ? 0 : 1;
      const diff = aHas - bHas;
      if (diff !== 0) return sortDir === "asc" ? diff : -diff;
      return a.email.localeCompare(b.email);
    });
  }, [
    fullyFilteredUsers,
    sortCol,
    sortDir,
    countryNameById,
    storeRefToCountryId,
  ]);

  const totalPages = Math.max(1, Math.ceil(sortedUsers.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const paginatedUsers = sortedUsers.slice(
    (safePage - 1) * PAGE_SIZE,
    safePage * PAGE_SIZE
  );

  return {
    // auth
    isPlatformOwner,
    isCheckingRole,
    // country / store tree
    countryNodes,
    nodeNameByRef,
    storesByCountry,
    selectedCountryIds,
    isLoadingCountries,
    isLoadingStores,
    // user data
    mergedUsers,
    isLoadingUsers,
    isFetchingUsers,
    isErrorUsers,
    displayNames,
    // derived / filtered
    fullyFilteredUsers,
    countryNameById,
    storeRefToCountryId,
    // paginated
    paginatedUsers,
    safePage,
    totalPages,
  };
}
